import { useState } from 'react';
import { Switch, Typography } from '../ui';
import { CheckboxGroup, Collapsible, TextButton } from '../ui';
import { useTranslation } from '../../../lib/i18n';
import {
  bulkState,
  connectionLabel,
  groupIntoFamilies,
  lendSummary,
  narrowToRequired,
  setBulk,
  toggleTool,
  toolKey,
  type LendableConnection,
  type LendableState,
  type LendableTool,
} from '../../../lib/app-lending-flow';
import styles from './LendConnections.module.css';

/**
 * The part of the share dialog where an owner decides whether the person they are sharing with may act
 * on THEIR account, and with exactly which tools.
 *
 * <p><b>Off by default, and the copy says what "off" means.</b> An ordinary share hands over the app;
 * whoever opens it connects their own Symphony and sees their own data, so nothing they do reaches the
 * owner. That is not an absence of a feature, it is the safe thing this screen is protecting, and the
 * line under the switch says so rather than leaving the reader to assume.
 *
 * <p><b>Tools, one at a time — never a connection as a whole.</b> A checkbox reading "Home Assistant"
 * would grant every verb that connection ever gains, including ones that do not exist yet. What a
 * person ticks here is what the gate enforces, so the list they read has to be the list that is true.
 *
 * <p><b>Arranged, because a true list is not automatically a readable one.</b> A Home Assistant
 * connection offers dozens of tools, and rendering them as one flat column of checkboxes under a
 * thirty-two character hostname is how a screen that is technically correct becomes unusable. So the
 * connection collapses to one line with a count, its tools group into families by subject, and every
 * level has a select-all. All of that is PRESENTATION: `chosen` is still a set of individual tools and
 * a bulk control is a loop over the same per-tool toggle, so nothing here can widen a grant beyond the
 * tools actually on screen.
 *
 * <p>Built from the house `Switch` / `CheckboxGroup` / `Collapsible` primitives rather than the
 * `@wix/design-system` barrel — the repo rule, and it also keeps the dialog's import graph out of
 * jest's reach (the barrel drags in a radix dependency this runner cannot resolve).
 */
export function LendConnections({
  lending,
  onLendingChange,
  lendable,
  chosen,
  onChosenChange,
  disabled,
  requiredTools,
  showAllTools,
  onShowAllToolsChange,
}: {
  lending: boolean;
  onLendingChange: (lending: boolean) => void;
  lendable: LendableState;
  chosen: ReadonlySet<string>;
  onChosenChange: (chosen: Set<string>) => void;
  disabled?: boolean;
  /** What the app itself declared it calls. Absent or unmatched means "show everything". */
  requiredTools?: string[];
  showAllTools?: boolean;
  onShowAllToolsChange?: (showAll: boolean) => void;
}) {
  const { t } = useTranslation();
  const summary = lendable.status === 'ready' ? lendSummary(lendable.connections, chosen) : null;
  // The list actually rendered. `narrowed` is false whenever narrowing would not help — nothing
  // declared, nothing matched, or the owner asked to see it all — and the summary above still counts
  // against the FULL list, because that is what a tick means regardless of what is on screen.
  const view =
    lendable.status === 'ready'
      ? narrowToRequired(lendable.connections, requiredTools, showAllTools === true)
      : { connections: [], narrowed: false, totalTools: 0 };

  return (
    <div className={styles.section}>
      <Switch
        checked={lending}
        disabled={disabled}
        onChange={onLendingChange}
        label={t('apps.share.lend.title')}
        // What OFF means, said out loud. Otherwise a reader has to infer it from the absence of a
        // list, and the inference people reach for is the wrong one.
        description={lending ? t('apps.share.lend.onHint') : t('apps.share.lend.offHint')}
      />

      {lending && lendable.status === 'loading' && (
        <Typography variant="p3" color="secondary" component="p">
          {t('apps.share.lend.loading')}
        </Typography>
      )}

      {/* A failed read is not "you have no connections". Saying the second about the first would have
          somebody share an app believing they had nothing to lend. */}
      {lending && lendable.status === 'error' && (
        <Typography variant="p3" color="secondary" component="p" className={styles.error}>
          {t('apps.share.lend.loadFailed')}
        </Typography>
      )}

      {lending && lendable.status === 'ready' && lendable.connections.length === 0 && (
        <Typography variant="p3" color="secondary" component="p">
          {t('apps.share.lend.noConnections')}
        </Typography>
      )}

      {lending &&
        lendable.status === 'ready' &&
        view.connections.map((connection) => (
          <ConnectionSection
            key={connection.id}
            connection={connection}
            chosen={chosen}
            onChosenChange={onChosenChange}
            disabled={disabled}
            // Opened by default when the list is already the app's own short one — there is nothing
            // to protect the reader from, and a two-row section behind a chevron is just a click.
            defaultOpen={view.narrowed}
          />
        ))}

      {/* The declaration is a builder's self-report, so it orders the list and never limits it. A
          wrong or stale one costs one click here, not the ability to lend. */}
      {lending && lendable.status === 'ready' && view.narrowed && (
        <TextButton size="small" disabled={disabled} onClick={() => onShowAllToolsChange?.(true)}>
          {t('apps.share.lend.showAll', { count: view.totalTools })}
        </TextButton>
      )}

      {/* The sentence they are agreeing to, in the terms that are true: a count and the connections by
          name. "Access to your connections" would describe forty tools as readily as two. */}
      {lending && summary ? (
        <Typography variant="p3" color="secondary" component="p" className={styles.summary}>
          {summary.kind === 'some' ? t(summary.key, summary.params) : t(summary.key)}
        </Typography>
      ) : null}
    </div>
  );
}

/**
 * One connection: a header that is always readable, and its tools behind it.
 *
 * <p>Starts CLOSED. Somebody who turned lending on is choosing between connections before they are
 * choosing between tools, and opening every connection to its full height buries that first decision
 * under the second. The count and the tick state stay visible while closed, so the row still answers
 * "have I lent anything here?" without being opened.
 */
function ConnectionSection({
  connection,
  chosen,
  onChosenChange,
  disabled,
  defaultOpen = false,
}: {
  connection: LendableConnection;
  chosen: ReadonlySet<string>;
  onChosenChange: (chosen: Set<string>) => void;
  disabled?: boolean;
  defaultOpen?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const families = groupIntoFamilies(connection.tools);
  const state = bulkState(chosen, connection.id, connection.tools);
  const tickedCount = connection.tools.filter((tool) => chosen.has(toolKey(connection.id, tool.name))).length;

  return (
    <div className={styles.connection}>
      <div className={styles.connectionHeader}>
        <button
          type="button"
          className={styles.disclosure}
          aria-expanded={open}
          onClick={() => setOpen((wasOpen) => !wasOpen)}
        >
          <span className={styles.chevron} data-open={open} aria-hidden="true" />
          {/* Shortened for width, never renamed — and the full address stays as the title, because the
              owner may well be telling two self-hosted boxes apart by it. */}
          <Typography variant="h4" color="strong" component="span" title={connection.name}>
            {connectionLabel(connection.name)}
          </Typography>
        </button>
        <Typography variant="p3" color="muted" component="span">
          {tickedCount > 0
            ? t('apps.share.lend.chosenCount', { count: tickedCount, total: connection.tools.length })
            : t(
                connection.tools.length === 1 ? 'apps.share.lend.toolCount.one' : 'apps.share.lend.toolCount.other',
                { count: connection.tools.length },
              )}
        </Typography>
        {connection.tools.length > 0 && (
          <TextButton
            size="small"
            disabled={disabled}
            onClick={() => onChosenChange(setBulk(chosen, connection.id, connection.tools, state !== 'all'))}
          >
            {state === 'all' ? t('apps.share.lend.clear') : t('apps.share.lend.selectAll')}
          </TextButton>
        )}
      </div>

      {connection.tools.length === 0 ? (
        // Present but with nothing to offer. Dropping it entirely would read as a connection that has
        // gone missing from a screen about connections.
        <Typography variant="p3" color="secondary" component="p">
          {t('apps.share.lend.noTools')}
        </Typography>
      ) : (
        <Collapsible open={open} innerClassName={styles.families}>
          {families.map((family) => (
            <FamilyGroup
              key={family.key}
              connectionId={connection.id}
              // The catch-all is titled here rather than in the grouping, which deals in tool names
              // and holds no copy.
              label={family.label ?? t('apps.share.lend.otherFamily')}
              tools={family.tools}
              chosen={chosen}
              onChosenChange={onChosenChange}
              disabled={disabled}
              // A single family is the connection's own list with a redundant heading over it.
              showHeader={families.length > 1}
            />
          ))}
        </Collapsible>
      )}
    </div>
  );
}

function FamilyGroup({
  connectionId,
  label,
  tools,
  chosen,
  onChosenChange,
  disabled,
  showHeader,
}: {
  connectionId: string;
  label: string;
  tools: LendableTool[];
  chosen: ReadonlySet<string>;
  onChosenChange: (chosen: Set<string>) => void;
  disabled?: boolean;
  showHeader: boolean;
}) {
  const { t } = useTranslation();
  const state = bulkState(chosen, connectionId, tools);

  return (
    <div className={styles.family}>
      {showHeader && (
        <div className={styles.familyHeader}>
          <Typography variant="p3" color="secondary" component="h5" className={styles.familyName}>
            {label}
          </Typography>
          <TextButton
            size="small"
            disabled={disabled}
            onClick={() => onChosenChange(setBulk(chosen, connectionId, tools, state !== 'all'))}
          >
            {state === 'all' ? t('apps.share.lend.clear') : t('apps.share.lend.selectAll')}
          </TextButton>
        </div>
      )}
      <CheckboxGroup
        aria-label={label}
        disabled={disabled}
        value={tools.map((tool) => toolKey(connectionId, tool.name)).filter((key) => chosen.has(key))}
        onChange={(next) => {
          // Diffed against what is on screen for THIS family, so ticks elsewhere are untouched.
          const wanted = new Set(next);
          let updated = new Set(chosen);
          for (const tool of tools) {
            const key = toolKey(connectionId, tool.name);
            if (wanted.has(key) !== updated.has(key)) {
              updated = toggleTool(updated, connectionId, tool.name);
            }
          }
          onChosenChange(updated);
        }}
        options={tools.map((tool) => ({
          value: toolKey(connectionId, tool.name),
          // MONOSPACED, because it is an identifier and not prose: somebody choosing between
          // light_turn_on and light_get_state is reading a name that must not be smoothed into
          // something friendlier than it is.
          label: <span className={styles.toolName}>{tool.name}</span>,
          ...(tool.description ? { description: tool.description } : {}),
        }))}
      />
    </div>
  );
}
